import { useEffect, useState } from 'react';
import { X, Phone, Mail, MapPin, Loader2, ChevronRight, ChevronDown, Users, CalendarClock, ShoppingBag, MessageSquare, PhoneIncoming, PhoneOutgoing, PhoneMissed } from 'lucide-react';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import usePotvrzeni from '../ui/usePotvrzeni';
import { SECTION_COLOR, KANALY, fmtKc, fmtCas, fmtDen, fmtDatum, StavOdznak, Nadpis, hlavicky } from '../objednavky/ui';
import { STAVY as STAVY_REZERVACE, cas as casRezervace } from '../rezervace/rezervaceUi';
import { FAZE, FazeOdznak, UrovenOdznak, predDny } from './ui';

/**
 * Zákazníci → karta zákazníka (detail ve sloupci vedle seznamu, na mobilu
 * celá obrazovka se šipkou zpět). Útrata a objednávky ze všech zdrojů
 * (Objedname i POS), oblíbená jídla, průběh po měsících, body a interní
 * poznámka — ta jediná se tu upravuje (stejnou čte obsluha v POS).
 *
 * Historie hosta je jedna osa: objednávky, rezervace a od 2026-10-12 i SMS
 * a hovory (`komunikace` ze serveru — odchozí a příchozí SMS, hovory
 * telefonu bistra).
 */
export default function ZakaznikKarta({ id, isDark, isMobile, mutedColor, onZpet, onObjednavka, onRezervace, onZakaznik, onZmena, onKomunikace = null, rezervaDole = null }) {
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [poznamka, setPoznamka] = useState('');
    const [uklada, setUklada] = useState(false);
    const [ulozeno, setUlozeno] = useState(false);
    const [bodyOtevrene, setBodyOtevrene] = useState(false);
    const [verze, setVerze] = useState(0);
    const [slucuje, setSlucuje] = useState(null);
    const [chybaSlouceni, setChybaSlouceni] = useState(null);
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);

    useEffect(() => {
        let platne = true;
        setData(null); setChyba(null); setUlozeno(false);
        fetch(`/api/zakaznici/${id}`, { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status === 404 ? 'Zákazník neexistuje.' : 'Kartu se nepodařilo načíst.'))))
            .then(d => { if (platne) { setData(d); setPoznamka(d.zakaznik.poznamka_interni || ''); } })
            .catch(e => { if (platne) setChyba(e.message); });
        return () => { platne = false; };
    }, [id, verze]);

    const cara = isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-subtle)';
    const muted = mutedColor;
    const sekce = { padding: isMobile ? '14px 16px' : '16px 22px', borderTop: `1px solid ${cara}` };

    if (chyba) return <p className="text-sm text-center" style={{ color: muted, padding: 40 }}>{chyba}</p>;
    if (!data) return <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: muted }}><Loader2 size={24} className="animate-spin" /></div>;

    const z = data.zakaznik;
    const s = data.souhrn;
    const zmenena = (poznamka || '') !== (z.poznamka_interni || '');
    const ulozPoznamku = async () => {
        setUklada(true);
        try {
            const res = await fetch(`/api/zakaznici/${id}`, { method: 'PATCH', headers: hlavicky(), body: JSON.stringify({ poznamka_interni: poznamka }) });
            if (res.ok) {
                const d = await res.json();
                setData(x => ({ ...x, zakaznik: { ...x.zakaznik, poznamka_interni: d.poznamka_interni } }));
                setUlozeno(true);
                onZmena?.();
            }
        } finally { setUklada(false); }
    };

    const maxMesic = Math.max(1, ...data.mesice.map(m => m.t));
    const kanaly = Object.entries(KANALY).map(([k, label]) => ({ k, label, ...(data.kanaly?.[k] || { pocet: 0, trzby: 0 }) })).filter(k => k.pocet > 0);
    const adresy = [...new Set([z.adresa, ...((Array.isArray(z.adresy) ? z.adresy : []).map(a => (typeof a === 'string' ? a : a?.adresa)))].filter(Boolean))];
    // Kontakty (2026-10-25): víc telefonů a e-mailů pod jednou kartou, se štítkem.
    const kontaktyTel = (z.kontakty || []).filter(k => k.typ === 'telefon');
    const telefony = kontaktyTel.length
        ? kontaktyTel.map(k => k.hodnota)
        : [...new Set([z.telefon, ...(Array.isArray(z.telefony) ? z.telefony : [])].filter(Boolean))];
    const popisTelefonu = Object.fromEntries(kontaktyTel.map(k => [k.hodnota, [k.stitek, k.hlavni && kontaktyTel.length > 1 ? 'hlavní' : null].filter(Boolean).join(' · ')]));
    const emaily = (z.kontakty || []).some(k => k.typ === 'email')
        ? z.kontakty.filter(k => k.typ === 'email').map(k => k.hodnota)
        : [z.email].filter(Boolean);
    const sloucit = async (d) => {
        setChybaSlouceni(null);
        const r = await fetch(`/api/zakaznici/${id}/slouceni/${d.id}`, { headers: { Accept: 'application/json' } });
        if (!r.ok) { setChybaSlouceni('Náhled sloučení se nepodařilo načíst.'); return; }
        const n = await r.json();
        if (!n.jde) { setChybaSlouceni(n.duvod); return; }
        const p = n.presune;
        const co = [
            `${p.objednavky} ${p.objednavky === 1 ? 'objednávka' : 'objednávek'}`,
            p.rezervace ? `${p.rezervace} rezervací` : null,
            p.hovory ? `${p.hovory} hovorů a SMS` : null,
            n.body ? `${n.body} bodů` : null,
            n.kontakty.length ? `kontakty ${n.kontakty.join(', ')}` : null,
        ].filter(Boolean).join(', ');
        const jmeno = d.jmeno || 'bez jména';
        const ok = await potvrdit(`Sloučit kartu „${jmeno}“ do této?`, {
            text: `Přesune se sem: ${co}.\nKarta „${jmeno}“ pak zanikne. Sloučení nejde vrátit zpět.`,
            potvrdit: 'Sloučit',
        });
        if (!ok) return;
        setSlucuje(d.id);
        try {
            const res = await fetch(`/api/zakaznici/${id}/sloucit`, { method: 'POST', headers: hlavicky(), body: JSON.stringify({ zdroj_id: d.id }) });
            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                setChybaSlouceni(e.message || 'Sloučení se nepovedlo.');
                return;
            }
            setVerze(v => v + 1);
            onZmena?.();
        } finally { setSlucuje(null); }
    };
    const posledniPohyb = data.body_transakce?.[0]?.datum;
    // Jedna osa: časy na milisekundy (rezervace přijdou jako ISO s posunem, objednávky jako místní čas)
    const ted = Date.now();
    const ms = (t) => { const v = new Date(String(t || '').replace(' ', 'T')).getTime(); return Number.isFinite(v) ? v : 0; };
    const udalosti = [
        ...(data.rezervace || []).map(r => ({ typ: 'rezervace', r, kdy: ms(r.zacatek), budouci: ms(r.zacatek) > ted && r.stav === 'potvrzena' })),
        ...data.objednavky.map(o => ({ typ: 'objednavka', o, kdy: ms(o.cas || o.datum), budouci: false })),
        ...(data.komunikace || []).map(k => ({ typ: 'kom', k, kdy: ms(k.kdy), budouci: false })),
    ].sort((a, b) => (a.budouci !== b.budouci ? (a.budouci ? -1 : 1) : (a.budouci ? a.kdy - b.kdy : b.kdy - a.kdy)));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <div style={{ padding: isMobile ? '6px 16px 12px' : '16px 22px 14px', flexShrink: 0 }}>
                {isMobile && <ZpetTlacitko onClick={onZpet} popis="Zpět na zákazníky" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
                <div className="flex items-start" style={{ gap: 12 }}>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                            <h2 className="truncate" style={{ margin: 0, fontSize: isMobile ? 19 : 18, fontWeight: 700, color: 'var(--text-primary)' }}>{z.jmeno || 'Bez jména'}</h2>
                            <FazeOdznak faze={s.faze} isDark={isDark} />
                            {s.pocet_12m > 0 && <UrovenOdznak uroven={z.uroven} isDark={isDark} />}
                        </div>
                        <p style={{ margin: '3px 0 0', fontSize: 12.5, color: muted }}>
                            {FAZE[s.faze]?.popis}
                            {z.registrovany ? ' · účet v Objedname' : ''}
                            {z.zdroj === 'pos' ? ' · založen na pokladně' : ''}
                        </p>
                    </div>
                    {!isMobile && (
                        <button type="button" onClick={onZpet} title="Zpět na přehled" aria-label="Zpět na přehled" className="flex items-center justify-center rounded-lg"
                            style={{ width: 34, height: 34, flexShrink: 0, cursor: 'pointer', color: 'var(--text-secondary)', background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` }}>
                            <X size={16} />
                        </button>
                    )}
                </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {/* Čísla */}
                <div style={sekce}>
                    <div className="grid" style={{ gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: '12px 16px' }}>
                        <Cislo popis="Útrata za 12 měsíců" hodnota={fmtKc(s.utrata_12m)} muted={muted} velke />
                        <Cislo popis="Objednávek za 12 měsíců" hodnota={s.pocet_12m} muted={muted} velke />
                        <Cislo popis="Průměrná objednávka" hodnota={fmtKc(s.prumer)} muted={muted} />
                        <Cislo popis="Naposledy" hodnota={s.posledni ? predDny(s.dni_od_posledni) : '—'} muted={muted} cervene={s.faze === 'ohrozeny'} />
                    </div>
                    <p style={{ margin: '10px 0 0', fontSize: 12, color: muted }}>
                        Celkem {s.pocet} objednávek za {fmtKc(s.utrata)}{s.prvni ? ` od ${fmtDatum(s.prvni)}` : ''}
                    </p>
                    {/* Útrata po měsících — sloupce bez os, hodnota v titulku */}
                    <div className="flex items-end" style={{ gap: 3, height: 46, marginTop: 12 }} aria-label="Útrata po měsících">
                        {data.mesice.map(m => (
                            <div key={m.m} title={`${m.m}: ${fmtKc(m.t)}`} style={{ flex: 1, height: `${Math.max(3, (m.t / maxMesic) * 100)}%`, borderRadius: 2, background: m.t > 0 ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'), opacity: m.t > 0 ? 0.85 : 1 }} />
                        ))}
                    </div>
                    <div className="flex justify-between" style={{ fontSize: 10.5, color: muted, marginTop: 4 }}>
                        <span>{fmtMesic(data.mesice[0]?.m)}</span><span>{fmtMesic(data.mesice[data.mesice.length - 1]?.m)}</span>
                    </div>
                </div>

                {/* Kontakt */}
                <div style={sekce}>
                    <Nadpis muted={muted}>Kontakt</Nadpis>
                    <div className="flex flex-col" style={{ gap: 7, fontSize: 13.5 }}>
                        {telefony.map(t => (
                            <span key={t} className="flex items-center" style={{ gap: 12, minHeight: 24 }}>
                                <a href={`tel:${t}`} className="flex items-center" style={{ gap: 8, color: SECTION_COLOR, textDecoration: 'none' }}><Phone size={13} /> {t}</a>
                                {popisTelefonu[t] && <span style={{ fontSize: 12, color: muted }}>{popisTelefonu[t]}</span>}
                                {onKomunikace && (
                                    <button type="button" onClick={() => onKomunikace(t)} className="inline-flex items-center"
                                        style={{ gap: 4, fontSize: 12, color: muted, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', minHeight: 24 }}>
                                        <MessageSquare size={12} /> hovory a SMS <ChevronRight size={12} />
                                    </button>
                                )}
                            </span>
                        ))}
                        {emaily.map(e => <span key={e} className="flex items-center" style={{ gap: 8, color: 'var(--text-secondary)', wordBreak: 'break-all' }}><Mail size={13} style={{ flexShrink: 0 }} /> {e}</span>)}
                        {adresy.map(a => <span key={a} className="flex" style={{ gap: 8, color: 'var(--text-secondary)' }}><MapPin size={13} style={{ flexShrink: 0, marginTop: 3 }} /> {a}</span>)}
                        {!telefony.length && !z.email && <span style={{ color: muted }}>Bez kontaktu.</span>}
                    </div>
                    {data.duplicity?.length > 0 && (
                        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
                            <p className="flex items-center" style={{ margin: 0, gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}><Users size={13} /> Možná stejný člověk</p>
                            {data.duplicity.map(d => (
                                <div key={d.id} className="flex items-center" style={{ gap: 8, minHeight: 40 }}>
                                    <button type="button" onClick={() => onZakaznik?.(d.id)} className="flex items-center flex-1 min-w-0 text-left"
                                        style={{ gap: 8, minHeight: 36, fontSize: 12.5, color: 'var(--text-secondary)', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
                                        <span className="flex-1 truncate">
                                            {d.jmeno || 'bez jména'} · {d.telefon || d.email}
                                            <span style={{ color: muted }}> · {d.pocet_objednavek} obj.{d.body ? ` · ${d.body} b.` : ''}</span>
                                        </span>
                                        <ChevronRight size={14} style={{ color: muted, flexShrink: 0 }} />
                                    </button>
                                    {data.muze_upravit && (
                                        <button type="button" disabled={slucuje !== null} onClick={() => sloucit(d)} className="rounded-lg"
                                            style={{ minHeight: 32, padding: '0 10px', fontSize: 12, fontWeight: 600, flexShrink: 0, cursor: slucuje === null ? 'pointer' : 'default', color: SECTION_COLOR, background: 'transparent', border: `1px solid ${SECTION_COLOR}`, opacity: slucuje === null || slucuje === d.id ? 1 : 0.45 }}>
                                            {slucuje === d.id ? 'Slučuji…' : 'Sloučit sem'}
                                        </button>
                                    )}
                                </div>
                            ))}
                            {chybaSlouceni && <p role="alert" style={{ margin: '6px 0 0', fontSize: 12, color: '#EF4444' }}>{chybaSlouceni}</p>}
                        </div>
                    )}
                </div>

                <Souhlasy id={id} z={z} muzeUpravit={data.muze_upravit} sekce={sekce} muted={muted} isDark={isDark} isMobile={isMobile}
                    onZmena={(souhlasy) => setData(x => ({ ...x, zakaznik: { ...x.zakaznik, souhlasy } }))} />

                {/* Poznámka pro obsluhu */}
                <div style={sekce}>
                    <Nadpis muted={muted}>Poznámka pro obsluhu</Nadpis>
                    {data.muze_upravit ? (
                        <>
                            <textarea id={`poznamka-${id}`} value={poznamka} onChange={e => { setPoznamka(e.target.value); setUlozeno(false); }} rows={3} maxLength={1000}
                                placeholder="Alergie, oblíbené místo, „nezvonit, volat“… Uvidí ji obsluha na pokladně."
                                style={{ width: '100%', borderRadius: 10, padding: '9px 11px', fontSize: 13.5, resize: 'vertical', outline: 'none', color: 'var(--text-primary)', background: isDark ? 'rgba(0,0,0,0.3)' : 'var(--surface-raised)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` }} />
                            <div className="flex items-center" style={{ gap: 10, marginTop: 8 }}>
                                <button type="button" disabled={!zmenena || uklada} onClick={ulozPoznamku} className="rounded-lg"
                                    style={{ minHeight: 40, padding: '0 16px', width: isMobile ? '100%' : 'auto', fontSize: 13, fontWeight: 600, border: 'none', cursor: zmenena ? 'pointer' : 'default', color: '#fff', background: SECTION_COLOR, opacity: zmenena && !uklada ? 1 : 0.45 }}>
                                    {uklada ? 'Ukládám…' : 'Uložit poznámku'}
                                </button>
                                {ulozeno && !zmenena && <span style={{ fontSize: 12, color: muted }}>Uloženo</span>}
                            </div>
                        </>
                    ) : (
                        <p style={{ margin: 0, fontSize: 13.5, color: z.poznamka_interni ? 'var(--text-primary)' : muted }}>{z.poznamka_interni || 'Bez poznámky.'}</p>
                    )}
                </div>

                {/* Co si dává */}
                {(data.oblibene.length > 0 || kanaly.length > 0) && (
                    <div style={sekce}>
                        <div className="grid" style={{ gridTemplateColumns: isMobile ? '1fr' : '3fr 2fr', gap: 18 }}>
                            <div>
                                <Nadpis muted={muted}>Oblíbená jídla</Nadpis>
                                {data.oblibene.map(j => (
                                    <div key={j.nazev} className="flex justify-between" style={{ gap: 10, fontSize: 13, padding: '5px 0', borderBottom: `1px solid ${cara}` }}>
                                        <span className="truncate" style={{ color: 'var(--text-primary)' }}>{j.nazev}</span>
                                        <span style={{ color: muted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{j.kusu}×</span>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <Nadpis muted={muted}>Kanály</Nadpis>
                                {kanaly.map(k => (
                                    <div key={k.k} className="flex justify-between" style={{ gap: 10, fontSize: 13, padding: '5px 0', borderBottom: `1px solid ${cara}` }}>
                                        <span style={{ color: 'var(--text-primary)' }}>{k.label}</span>
                                        <span style={{ color: muted, fontVariantNumeric: 'tabular-nums' }}>{k.pocet} · {fmtKc(k.trzby)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* FofrKarta (2026-10-26) — body z knihy OS, když je zapnutá */}
                {data.fofrkarta?.zapnuto && (
                    <FofrKartaVypis id={id} fk={data.fofrkarta} muzeUpravit={data.muze_upravit} sekce={sekce} muted={muted} cara={cara} isDark={isDark} isMobile={isMobile}
                        onObjednavka={onObjednavka} onZmena={() => { setVerze(v => v + 1); onZmena?.(); }} />
                )}

                {/* Body z Objedname — dokud FofrKarta není zapnutá, jinak jako historie */}
                <div style={sekce}>
                    <Nadpis muted={muted}>{data.fofrkarta?.zapnuto ? 'Historie bodů z Objedname' : 'Věrnostní body'}</Nadpis>
                    <div className="flex items-baseline flex-wrap" style={{ gap: '4px 16px', fontSize: 13 }}>
                        {!data.fofrkarta?.zapnuto && <span><b style={{ fontSize: 16, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{z.body.toLocaleString('cs-CZ')}</b> <span style={{ color: muted }}>zůstatek podle Objedname</span></span>}
                        <span style={{ color: muted }}>uplatněno celkem {fmtKc(s.body_uplatneno)}</span>
                    </div>
                    {data.body_transakce?.length > 0 && (
                        <>
                            <button type="button" onClick={() => setBodyOtevrene(o => !o)} className="flex items-center"
                                style={{ gap: 6, marginTop: 8, minHeight: 32, fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
                                {bodyOtevrene ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Pohyby bodů · poslední {fmtDatum(posledniPohyb)}
                            </button>
                            {bodyOtevrene && data.body_transakce.map((b, i) => (
                                <div key={i} className="flex justify-between" style={{ gap: 10, fontSize: 12.5, padding: '4px 0', borderBottom: `1px solid ${cara}` }}>
                                    <span style={{ color: 'var(--text-secondary)' }}>{fmtDatum(b.datum)} · {b.typ}{b.cislo_objednavky ? ` · ${b.cislo_objednavky}` : ''}</span>
                                    <b style={{ color: Number(b.castka) < 0 ? 'var(--text-secondary)' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{Number(b.castka) > 0 ? '+' : ''}{Math.round(Number(b.castka))}</b>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {/* Historie hosta — objednávky, rezervace, SMS a hovory na jedné ose
                    (od 2026-10-06; SMS a hovory od 10-12). Nadcházející rezervace nahoře. */}
                <div style={sekce}>
                    <Nadpis muted={muted}>
                        Historie hosta
                        {data.rezervace_souhrn?.pocet > 0 && (
                            <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
                                {' '}· {data.rezervace_souhrn.pocet} rezervací{data.rezervace_souhrn.nedorazila ? `, ${data.rezervace_souhrn.nedorazila}× nedorazili` : ''}
                            </span>
                        )}
                    </Nadpis>
                    {udalosti.length === 0 && <p style={{ margin: 0, fontSize: 13, color: muted }}>Zatím nic.</p>}
                    {udalosti.map(u => (u.typ === 'kom' ? (
                        <Komunikace key={`k${u.k.druh}${u.k.id}`} k={u.k} cara={cara} muted={muted} isMobile={isMobile} />
                    ) : u.typ === 'rezervace' ? (
                        <button key={`r${u.r.id}`} type="button" onClick={() => onRezervace?.(u.r)} className="flex items-center w-full text-left"
                            style={{
                                gap: 10, minHeight: isMobile ? 52 : 40, padding: u.budouci ? '4px 8px' : '4px 0', borderRadius: u.budouci ? 8 : 0,
                                background: u.budouci ? `${SECTION_COLOR}14` : 'transparent', border: 'none', borderBottom: `1px solid ${cara}`,
                                cursor: onRezervace ? 'pointer' : 'default', opacity: u.r.stav === 'zrusena' ? 0.55 : 1,
                            }}>
                            <CalendarClock size={15} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                            <span className="flex-1 min-w-0">
                                <span style={{ display: 'block', fontSize: 13, color: 'var(--text-primary)' }}>
                                    {fmtDen(u.r.datum, true)} <span style={{ color: muted }}>{u.r.cely_den ? 'celý den' : casRezervace(u.r.zacatek)}</span>
                                </span>
                                <span className="truncate" style={{ display: 'block', fontSize: 11.5, color: muted }}>
                                    Rezervace · {u.r.pocet_hostu} os.{u.r.poznamka ? ` · ${u.r.poznamka}` : ''}
                                </span>
                            </span>
                            <span className="rounded-full" style={{ padding: '1px 8px', fontSize: 10.5, fontWeight: 600, flexShrink: 0, background: `${(STAVY_REZERVACE[u.r.stav] || STAVY_REZERVACE.potvrzena).color}26`, color: (STAVY_REZERVACE[u.r.stav] || STAVY_REZERVACE.potvrzena).color }}>
                                {(STAVY_REZERVACE[u.r.stav] || STAVY_REZERVACE.potvrzena).label}
                            </span>
                            {onRezervace && <ChevronRight size={14} style={{ color: muted, flexShrink: 0 }} />}
                        </button>
                    ) : (
                        <button key={`o${u.o.id}`} type="button" onClick={() => onObjednavka?.(u.o.id)} className="flex items-center w-full text-left"
                            style={{ gap: 10, minHeight: isMobile ? 52 : 40, padding: '4px 0', background: 'transparent', border: 'none', borderBottom: `1px solid ${cara}`, cursor: onObjednavka ? 'pointer' : 'default', opacity: u.o.kategorie === 'storno' ? 0.6 : 1 }}>
                            <ShoppingBag size={15} style={{ color: muted, flexShrink: 0 }} />
                            <span className="flex-1 min-w-0">
                                <span style={{ display: 'block', fontSize: 13, color: 'var(--text-primary)' }}>{fmtDen(u.o.datum, true)} <span style={{ color: muted }}>{fmtCas(u.o.cas)}</span></span>
                                <span className="truncate" style={{ display: 'block', fontSize: 11.5, color: muted }}>{KANALY[u.o.kanal] || u.o.kanal}{u.o.zdroj === 'pos' ? ' · pokladna' : ''}</span>
                            </span>
                            {u.o.kategorie !== 'hotova' && <StavOdznak o={u.o} isDark={isDark} maly />}
                            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', minWidth: 72, textAlign: 'right' }}>{fmtKc(u.o.celkem_s_dph)}</span>
                            {onObjednavka && <ChevronRight size={14} style={{ color: muted, flexShrink: 0 }} />}
                        </button>
                    )))}
                </div>
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
            {potvrzeni}
        </div>
    );
}

const POPIS_TYPU = { cashback: 'Cashback', uplatneni: 'Sleva za body', vraceni: 'Vrácené body', storno: 'Storno', prenos: 'Přeneseno z Objedname', propadnuti: 'Propadlé body', smenarna: 'Směnárna FofrSvět', rucne: 'Ruční úprava' };

/**
 * FofrKarta na kartě hosta (2026-10-26): zůstatek, čekající cashback,
 * nejbližší propadnutí a výpis pohybů jako z banky. Ruční úprava s důvodem
 * — důvod je vidět ve výpisu.
 */
function FofrKartaVypis({ id, fk, muzeUpravit, sekce, muted, cara, isDark, isMobile, onObjednavka, onZmena }) {
    const [otevrene, setOtevrene] = useState(false);
    const [uprava, setUprava] = useState(null); // { body, duvod }
    const [chyba, setChyba] = useState(null);
    const [prace, setPrace] = useState(false);
    const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('cs-CZ') : '');

    const ulozit = async () => {
        setPrace(true); setChyba(null);
        try {
            const r = await fetch(`/api/fofrkarta/zakaznik/${id}/upravit`, { method: 'POST', headers: hlavicky(), body: JSON.stringify({ body: Number(uprava.body), duvod: uprava.duvod }) });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.message || 'Body se nepodařilo upravit.');
            setUprava(null);
            onZmena?.();
        } catch (e) { setChyba(e.message); } finally { setPrace(false); }
    };
    const pole = { height: 38, borderRadius: 8, padding: '0 10px', fontSize: 13, outline: 'none', color: 'var(--text-primary)', background: isDark ? 'rgba(0,0,0,0.3)' : 'var(--surface-raised)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` };

    return (
        <div style={sekce}>
            <Nadpis muted={muted}>FofrKarta</Nadpis>
            <div className="flex items-baseline flex-wrap" style={{ gap: '4px 16px', fontSize: 13 }}>
                <span><b style={{ fontSize: 16, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fk.zustatek.toLocaleString('cs-CZ')}</b> <span style={{ color: muted }}>Fofrbodů = {fk.zustatek.toLocaleString('cs-CZ')} Kč</span></span>
                {fk.ceka > 0 && <span style={{ color: muted }}>+{fk.ceka} čeká na dokončení objednávek</span>}
                {fk.nejblizsi_propadnuti && <span style={{ color: muted }}>{fk.nejblizsi_propadnuti.body} propadne {datum(fk.nejblizsi_propadnuti.datum)}</span>}
            </div>
            {fk.pohyby.length > 0 && (
                <button type="button" onClick={() => setOtevrene(o => !o)} className="flex items-center"
                    style={{ gap: 6, marginTop: 8, minHeight: 32, fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
                    {otevrene ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Výpis bodů · {fk.pohyby.length} pohybů
                </button>
            )}
            {otevrene && fk.pohyby.map(p => (
                <div key={p.id} className="flex items-center" style={{ gap: 10, fontSize: 12.5, padding: '5px 0', borderBottom: `1px solid ${cara}`, opacity: p.stav === 'ceka' ? 0.7 : 1 }}>
                    <span className="flex-1 min-w-0" style={{ color: 'var(--text-secondary)' }}>
                        {datum(p.kdy)} · {p.popis || POPIS_TYPU[p.typ] || p.typ}
                        {p.cislo_objednavky && (onObjednavka && p.objednavka_id
                            ? <> · <button type="button" onClick={() => onObjednavka(p.objednavka_id)} style={{ color: SECTION_COLOR, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5 }}>{p.cislo_objednavky}</button></>
                            : ` · ${p.cislo_objednavky}`)}
                        {p.stav === 'ceka' && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600 }}>čeká na dokončení</span>}
                        {p.plati_do && p.stav === 'pripsano' && <span style={{ display: 'block', fontSize: 11, color: muted }}>platí do {datum(p.plati_do)}</span>}
                    </span>
                    <b style={{ color: p.body < 0 ? 'var(--text-secondary)' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{p.body > 0 ? '+' : ''}{p.body}</b>
                </div>
            ))}
            {muzeUpravit && !uprava && (
                <button type="button" onClick={() => setUprava({ body: '', duvod: '' })}
                    style={{ marginTop: 8, minHeight: 32, fontSize: 12.5, fontWeight: 600, color: SECTION_COLOR, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
                    Připsat nebo odečíst body
                </button>
            )}
            {uprava && (
                <div className="flex flex-wrap items-center" style={{ gap: 8, marginTop: 10 }}>
                    <input id={`fk-body-${id}`} type="number" value={uprava.body} onChange={e => setUprava(u => ({ ...u, body: e.target.value }))} placeholder="+50 / −20" style={{ ...pole, width: 110 }} />
                    <input id={`fk-duvod-${id}`} value={uprava.duvod} onChange={e => setUprava(u => ({ ...u, duvod: e.target.value }))} maxLength={150} placeholder="Důvod — uvidí ho ve výpisu" style={{ ...pole, flex: 1, minWidth: isMobile ? '100%' : 180 }} />
                    <button type="button" disabled={prace || !Number(uprava.body) || !uprava.duvod.trim()} onClick={ulozit} className="rounded-lg"
                        style={{ minHeight: 38, padding: '0 14px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', color: '#fff', background: SECTION_COLOR, opacity: prace || !Number(uprava.body) || !uprava.duvod.trim() ? 0.45 : 1 }}>
                        Uložit
                    </button>
                    <button type="button" onClick={() => { setUprava(null); setChyba(null); }} style={{ minHeight: 38, fontSize: 13, color: muted, background: 'transparent', border: 'none', cursor: 'pointer' }}>Zrušit</button>
                </div>
            )}
            {chyba && <p role="alert" style={{ margin: '6px 0 0', fontSize: 12, color: '#EF4444' }}>{chyba}</p>}
        </div>
    );
}

/**
 * Souhlasy a narozeniny. E-mail smí každý s adresou (povolují to obchodní
 * podmínky), jen se může odhlásit; SMS jen s výslovným souhlasem.
 * Narozeniny pro narozeninovou kampaň.
 */
function Souhlasy({ id, z, muzeUpravit, sekce, muted, isDark, isMobile, onZmena }) {
    const s = z.souhlasy || {};
    const [prace, setPrace] = useState(null);
    const [chyba, setChyba] = useState(null);
    const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('cs-CZ') : '');

    const uloz = async (klic, zmena) => {
        setPrace(klic); setChyba(null);
        try {
            const r = await fetch(`/api/zakaznici/${id}`, { method: 'PATCH', headers: hlavicky(), body: JSON.stringify(zmena) });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.message || 'Uložení se nepovedlo.');
            onZmena(j.souhlasy);
        } catch (e) { setChyba(e.message); }
        finally { setPrace(null); }
    };

    const radek = (popis, stav, detail, akce) => (
        <div className="flex items-center" style={{ gap: 10, minHeight: isMobile ? 48 : 36, fontSize: 13.5 }}>
            <span style={{ width: 92, flexShrink: 0, color: muted, fontSize: 12.5 }}>{popis}</span>
            <span className="flex-1 min-w-0">
                <span style={{ color: stav ? 'var(--text-primary)' : muted, fontWeight: stav ? 600 : 400 }}>{stav ? 'smí' : 'nesmí'}</span>
                {detail && <span style={{ color: muted, fontSize: 12 }}> · {detail}</span>}
            </span>
            {akce}
        </div>
    );
    const tlacitko = (klic, text, onClick) => muzeUpravit ? (
        <button type="button" disabled={!!prace} onClick={onClick}
            style={{ height: isMobile ? 40 : 30, padding: '0 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, color: 'var(--text-primary)', background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }}>
            {prace === klic ? '…' : text}
        </button>
    ) : null;

    return (
        <div style={sekce}>
            <Nadpis muted={muted}>Souhlasy se zprávami</Nadpis>
            {radek('E-mail', s.email,
                !z.email ? 'bez e-mailu' : s.email_odhlasen_v ? `odhlášen ${datum(s.email_odhlasen_v)}` : 'podle obchodních podmínek',
                z.email && tlacitko('email', s.email_odhlasen_v ? 'Zrušit odhlášení' : 'Odhlásit', () => uloz('email', { email_odhlasen: !s.email_odhlasen_v })))}
            {radek('SMS', s.sms,
                s.sms ? `souhlas ${datum(s.sms_souhlas_v)}${s.zdroj ? ` · ${ZDROJ_SOUHLASU[s.zdroj] ?? s.zdroj}` : ''}` : s.sms_odhlasen_v ? `nechce · ${datum(s.sms_odhlasen_v)}${s.zdroj ? ` · ${ZDROJ_SOUHLASU[s.zdroj] ?? s.zdroj}` : ''}` : 'zatím neodpověděl',
                tlacitko('sms', s.sms ? 'Odhlásit' : 'Zapsat souhlas', () => uloz('sms', { sms_souhlas: !s.sms })))}
            <div className="flex items-center" style={{ gap: 10, minHeight: isMobile ? 48 : 36, fontSize: 13.5 }}>
                <span style={{ width: 92, flexShrink: 0, color: muted, fontSize: 12.5 }}>Narozeniny</span>
                {muzeUpravit ? (
                    <input type="date" defaultValue={s.narozeniny || ''} max={new Date().toISOString().slice(0, 10)}
                        onBlur={e => (e.target.value || null) !== (s.narozeniny || null) && uloz('narozeniny', { narozeniny: e.target.value || null })}
                        style={{ height: isMobile ? 40 : 30, borderRadius: 8, padding: '0 8px', fontSize: 13, color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
                            background: isDark ? 'rgba(0,0,0,0.3)' : 'var(--surface-raised)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` }} />
                ) : (
                    <span style={{ color: s.narozeniny ? 'var(--text-primary)' : muted }}>{s.narozeniny ? datum(s.narozeniny) : 'neuvedeny'}</span>
                )}
            </div>
            {chyba && <p style={{ margin: '6px 0 0', fontSize: 12, color: '#F87171' }}>{chyba}</p>}
            <p style={{ margin: '6px 0 0', fontSize: 11.5, color: muted }}>SMS se zapisuje jen s výslovným souhlasem hosta (např. ústně u pokladny). Každý marketingový e-mail ponese odkaz na odhlášení.</p>
        </div>
    );
}

/** Odkud souhlas / odmítnutí přišlo — kód ze serveru na čitelný text. */
const ZDROJ_SOUHLASU = {
    stranka_objednavky: 'na stránce objednávky (odkaz ze SMS)',
    pokladna_telefon: 'po telefonu na pokladně',
};
const SMS_TYP = {
    prvni_sms: 'Potvrzení objednávky',
    potvrzeni_objednavky: 'Potvrzení objednávky',
    odjezd: 'Jídlo je na cestě',
    pripraveno: 'Připraveno k vyzvednutí',
    nezastizen: 'Nezastihli jsme vás',
    za_rohem: 'Kurýr za rohem',
    uctenka_sms: 'Účtenka',
    pokladna: 'Z pokladny',
    os: 'Z OS',
    zkouska: 'Zkouška',
};
const HOVOR_STAV = {
    zmeskano: 'zmeškaný',
    nedostupne: 'nedovoláno',
    zvoni: 'zvoní',
    probiha: 'probíhá',
    vytaci: 'vytáčí se',
};

/** SMS nebo hovor na časové ose karty — jen pro čtení. */
function Komunikace({ k, cara, muted, isMobile }) {
    const d = new Date(k.kdy);
    const den = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const cas = d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
    const zmeskany = k.druh === 'hovor' && k.stav === 'zmeskano';
    const chyba = k.druh === 'sms_ven' && k.stav === 'chyba';
    let Ikona = MessageSquare, popis, text = null;
    if (k.druh === 'hovor') {
        Ikona = zmeskany ? PhoneMissed : k.smer === 'odchozi' ? PhoneOutgoing : PhoneIncoming;
        const delka = k.delka_s ? ` · ${Math.floor(k.delka_s / 60)}:${String(k.delka_s % 60).padStart(2, '0')}` : '';
        popis = `${k.smer === 'odchozi' ? 'Odchozí hovor' : 'Příchozí hovor'}${HOVOR_STAV[k.stav] ? ` · ${HOVOR_STAV[k.stav]}` : ''}${delka}`;
    } else if (k.druh === 'sms_dovnitr') {
        popis = 'SMS od hosta';
        text = k.text;
    } else {
        popis = `SMS · ${SMS_TYP[k.typ] || k.typ}${k.test ? ' · test, neodesláno' : ''}${chyba ? ' · chyba' : ''}`;
        text = k.text;
    }
    const barva = zmeskany || chyba ? '#F87171' : muted;
    return (
        <div className="flex items-start w-full" style={{ gap: 10, minHeight: isMobile ? 52 : 40, padding: '8px 0', borderBottom: `1px solid ${cara}` }}>
            <Ikona size={15} style={{ color: barva, flexShrink: 0, marginTop: 2 }} />
            <span className="flex-1 min-w-0">
                <span style={{ display: 'block', fontSize: 13, color: 'var(--text-primary)' }}>{fmtDen(den, true)} <span style={{ color: muted }}>{cas}</span></span>
                <span style={{ display: 'block', fontSize: 11.5, color: barva }}>{popis}</span>
                {text && <span className="line-clamp-2" style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{text}</span>}
            </span>
        </div>
    );
}

function fmtMesic(m) {
    if (!m) return '';
    const [y, mo] = m.split('-').map(Number);
    return new Date(y, mo - 1, 1).toLocaleDateString('cs-CZ', { month: 'short', year: '2-digit' });
}

function Cislo({ popis, hodnota, muted, velke = false, cervene = false }) {
    return (
        <div className="min-w-0">
            <span style={{ display: 'block', fontSize: 11, color: muted }}>{popis}</span>
            <span className="truncate" style={{ display: 'block', fontSize: velke ? 20 : 15, fontWeight: 800, color: cervene ? '#F87171' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.25 }}>{hodnota}</span>
        </div>
    );
}
